import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import Card from "../../../UI/Card/Card";
import Button from "../../../UI/Button/Button"
import ErrorPanel, { ErrorSize } from "../../../UI/ErrorPanel/ErrorPanel";
import { faEdit, faTrashAlt } from "@fortawesome/free-solid-svg-icons";

import * as PushNotificationUtils from "../../../Utils/PushNotificationUtils";
import * as ApplicationsUtils from "../../../Utils/ApplicationsUtils";
import * as ApplicationsActions from "../../../../middleware/Store/Actions/ApplicationsActions";
import * as SandBoxApplicationsActions from "../../../../middleware/Store/Actions/SandBoxApplicationsActions";
import * as ErrorUtils from "../../../Utils/ErrorUtils";
import LogService from "../../../../middleware/CommonSdk/LogService";

const LOG_TAG = "APP-EDITION-ANDROID-PNS";

const AndroidPns = (props) => {
    const intl = useIntl();
    const [authorizationKey, setAuthorizationKey] = useState("");
    const [senderId, setSenderId] = useState("");
    const [activation, setActivation] = useState(true);
    const [application, setApplication] = useState(null);
    const [progressUpdate, setProgressUpdate] = useState(false);
    const [progressDelete, setProgressDelete] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        LogService.trace(LOG_TAG, "useEffect application");
        if (props.applicationId) {
            const foundApplication = ApplicationsUtils.getApplicationById(props.applicationId, props.applications);
            setApplication(foundApplication);
            setAuthorizationKey(PushNotificationUtils.getAuthorizationKey(PushNotificationUtils.getAndroidPn(ApplicationsUtils.getPushNotification(foundApplication))));
            setSenderId(PushNotificationUtils.getSenderId(PushNotificationUtils.getAndroidPn(ApplicationsUtils.getPushNotification(foundApplication))));
            setActivation(PushNotificationUtils.isEnabled(PushNotificationUtils.getAndroidPn(ApplicationsUtils.getPushNotification(foundApplication))));

            if (props.applicationId !== application?.id) {
                setProgressUpdate(false);
                setProgressDelete(false);
                setErrorMessage("");
            }
        }
    }, [props.applications, props.applicationId]);

    const handleAuthorizationKeyChange = (event) => {
        setAuthorizationKey(event.target.value);
    };

    const handleSenderIdChange = (event) => {
        setSenderId(event.target.value);
    };

    const handleActivationChange = (event) => {
        setActivation(event.target.value === "true");
    };

    const handleUpdate = (event) => {
        LogService.trace(LOG_TAG, "handleUpdate");

        setProgressUpdate(true);
        const pns = PushNotificationUtils.getAndroidPn(ApplicationsUtils.getPushNotification(application));
        let pnsId = pns?.id;
        let needDeleteDeprecatedFcmPush = Promise.resolve();
        if (pnsId && PushNotificationUtils.isFcmType(pns)) {
            LogService.debug(LOG_TAG, "handlePushUpdate", "remove fcm and create fcm_xmpp push notification");
            needDeleteDeprecatedFcmPush = props.pushDelete(application?.id, pnsId);
            pnsId = null;
        }

        needDeleteDeprecatedFcmPush
            .then(() => props.pushUpdate(application?.id, pnsId, activation, authorizationKey, senderId))
            .then(() => {
                LogService.debug(LOG_TAG, "handlePushUpdate", "needDeleteDeprecatedFcmPush success");
                setProgressUpdate(false);
            })
            .catch((errorPayload) => {
                LogService.debug(LOG_TAG, "handlePushUpdate", "needDeleteDeprecatedFcmPush error", errorPayload);
                setProgressUpdate(false);
                setErrorMessage(ErrorUtils.getPrettyErrorMessage(errorPayload, intl));
            });
    };

    const handleDelete = (event) => {
        LogService.trace(LOG_TAG, "handleDelete");

        setProgressDelete(true);
        const pns = PushNotificationUtils.getAndroidPn(ApplicationsUtils.getPushNotification(application));
        props.pushDelete(application?.id, pns.id)
            .then(() => {
                LogService.debug(LOG_TAG, "handleDelete success");
                setProgressDelete(false);
            })
            .catch((errorPayload) => {
                LogService.debug(LOG_TAG, "handleDelete error");
                setProgressDelete(false);
                setErrorMessage(ErrorUtils.getPrettyErrorMessage(errorPayload, intl));
            });
    };

    LogService.trace(LOG_TAG, "render");

    const disableUpdateButton = authorizationKey.length === 0 || senderId.length === 0;
    const pns = PushNotificationUtils.getAndroidPn(ApplicationsUtils.getPushNotification(application));

    let errorMessageDisplay = null;
    if (errorMessage) {
        errorMessageDisplay =
            <div id="android-error-message" className="mt-2">
                <ErrorPanel size={ErrorSize.Small} >{errorMessage}</ErrorPanel>
            </div>
    }

    const pushNotificationMessage = intl.formatMessage({ id: "applicationEditionAndroidMessage" }, { b: chunks => <b>{chunks}</b> });
    let cardDisplay = null;
    if (pns || ApplicationsUtils.isMobileApplication(application)) {
        cardDisplay =
            <Card title={intl.messages.applicationEditionAndroidTitle}>
                <div className="panel bg-fog mb-3">{pushNotificationMessage}</div>

                <div className="form-group">
                    <label className="input-label">{intl.messages.applicationEditionAndroidAuthorizationKeyTitle}</label>
                    <input
                        id="android-key-input"
                        value={authorizationKey}
                        type="text"
                        className="form-control"
                        placeholder={intl.messages.applicationEditionAndroidAuthorizationKeyPlaceholder}
                        autoComplete="off"
                        onChange={handleAuthorizationKeyChange}
                        disabled={!props.isUpdateEnabled}
                    />
                </div>
                <div className="form-group">
                    <label className="input-label">{intl.messages.applicationEditionAndroidSenderIdTitle}</label>
                    <input
                        id="android-senderid-input"
                        value={senderId}
                        type="text"
                        className="form-control"
                        placeholder={intl.messages.applicationEditionAndroidSenderIdPlaceholder}
                        autoComplete="off"
                        onChange={handleSenderIdChange}
                        disabled={!props.isUpdateEnabled}
                    />
                </div>
                <div className="form-group">
                    <label className="input-label">{intl.messages.applicationEditionAndroidActivationTitle}</label>
                    <select className="form-control mb-3"
                        id="android-activation-select"
                        value={activation}
                        onChange={handleActivationChange}
                        disabled={!props.isUpdateEnabled}
                    >
                        <option value={false} key={false}>{intl.messages.no} </option>
                        <option value={true} key={true}>{intl.messages.yes} </option>
                    </select>
                </div>

                {props.isUpdateEnabled &&
                    <div>
                        {pns?.id &&
                            <Button
                                id="android-delete-btn"
                                label={intl.messages.delete}
                                className="btn-danger mr-2"
                                icon={faTrashAlt}
                                onClick={handleDelete}
                                spinner={progressDelete}
                                disabled={progressDelete}
                            />
                        }
                        <Button
                            id="android-update-btn"
                            label={intl.messages.update}
                            icon={faEdit}
                            onClick={handleUpdate}
                            spinner={progressUpdate}
                            disabled={progressUpdate || disableUpdateButton}
                            className="btn-submit"
                        />
                        {errorMessageDisplay}
                    </div>
                }
            </Card >
    }

    return <div>{cardDisplay}</div>
}

AndroidPns.propTypes = {
    applicationId: PropTypes.string,
    isUpdateEnabled: PropTypes.bool,
    isSandBoxApplication: PropTypes.bool
};

AndroidPns.defaultProps = {
    applicationId: "",
    isUpdateEnabled: false,
    isSandBoxApplication: false
}

const mapStateToProps = (state, props) => {
    return {
        applications: props.isSandBoxApplication ? state.sandBoxApplications.applications : state.applications.applications
    };
};

const mapDispatchToProps = (dispatch, props) => {
    return {
        pushDelete: (appId, pnId) => dispatch(props.isSandBoxApplication ? SandBoxApplicationsActions.deletePushNotification(appId, pnId) : ApplicationsActions.deletePushNotification(appId, pnId)),
        pushUpdate: (appId, pnId, isEnabled, key, senderId) => dispatch(props.isSandBoxApplication ? SandBoxApplicationsActions.addOrUpdatePushNotificationAndroid(appId, pnId, isEnabled, key, senderId) : ApplicationsActions.addOrUpdatePushNotificationAndroid(appId, pnId, isEnabled, key, senderId))
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(AndroidPns);